import React, { Context, createContext, FC, ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { IUsers } from '../common/io/orm/users';
import { ICompanyPayment } from '../common/io/orm/company-payment';
import { LOCAL_STORAGE_COMPANY_GROUPS, LOCAL_STORAGE_COMPANY_PAYMENT, LOCAL_STORAGE_USER_DATA } from '../constants/localstorage.constants';
import { getFromLocalStorage, removeFromLocalStorage, setToLocalStorage } from '../utils/storage.util';
import { ICompanyGroup } from '../common/io/orm/company-group';

export interface IAuthContextProps {
	userData: IUsers | null;
	setUserData(...args: unknown[]): unknown;
	companyPayment: ICompanyPayment | null;
	setCompanyPayment(...args: unknown[]): unknown;
	companyGroups: ICompanyGroup[] | null;
	setCompanyGroups(...args: unknown[]): unknown;
}
const AuthContext: Context<IAuthContextProps> = createContext<IAuthContextProps>({} as IAuthContextProps);

interface IAuthContextProviderProps {
	children: ReactNode;
}

export const AuthContextProvider: FC<IAuthContextProviderProps> = ({ children }: IAuthContextProviderProps): ReactElement => {
	const [userData, setUserData] = useState<IUsers | null>((): IUsers | null => {
		return JSON.parse(getFromLocalStorage(LOCAL_STORAGE_USER_DATA));
	});

	const [companyPayment, setCompanyPayment] = useState<ICompanyPayment | null>((): ICompanyPayment | null => {
		return JSON.parse(getFromLocalStorage(LOCAL_STORAGE_COMPANY_PAYMENT));
	});

	const [companyGroups, setCompanyGroups] = useState<ICompanyGroup[] | null>((): ICompanyGroup[] | null => {
		return JSON.parse(getFromLocalStorage(LOCAL_STORAGE_COMPANY_GROUPS));
	});

	useEffect((): void => {
		if (userData) {
			setToLocalStorage(LOCAL_STORAGE_USER_DATA, JSON.stringify(userData));
		} else {
			removeFromLocalStorage(LOCAL_STORAGE_USER_DATA);
		}
	}, [userData]);

	useEffect((): void => {
		if (companyPayment) {
			setToLocalStorage(LOCAL_STORAGE_COMPANY_PAYMENT, JSON.stringify(companyPayment));
		} else {
			removeFromLocalStorage(LOCAL_STORAGE_COMPANY_PAYMENT);
		}
	}, [companyPayment]);

	useEffect((): void => {
		if (companyGroups) {
			setToLocalStorage(LOCAL_STORAGE_COMPANY_GROUPS, JSON.stringify(companyGroups));
		} else {
			removeFromLocalStorage(LOCAL_STORAGE_COMPANY_GROUPS);
		}
	}, [companyGroups]);

	const value: IAuthContextProps = useMemo(
		() => ({
			userData,
			setUserData,
			companyPayment,
			setCompanyPayment,
			companyGroups,
			setCompanyGroups,
		}),
		[userData, companyPayment, companyGroups, setCompanyGroups, companyGroups],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
AuthContextProvider.propTypes = {
	children: PropTypes.node.isRequired as any,
};

export default AuthContext;
